import { getDistance } from '../utils/math.js';
import { isDebugMode, debugLog } from '../utils/debug.js';

// Base AI class for all unit types
export class UnitAI {
  constructor(unit) {
    this.unit = unit;
    
    // Perception
    this.perceptionRange = 150;
    this.lastPerceptionUpdate = 0;
    this.perceptionUpdateInterval = 100; // ms
    
    // Target management
    this.currentTarget = null;
    this.lastTargetCheck = 0;
    this.targetCheckInterval = 500; // ms
    
    // Threat assessment
    this.threats = new Map(); // entity -> threat level
    this.lastThreatUpdate = 0;
    this.threatUpdateInterval = 200; // ms
    
    // Decision making
    this.lastDecision = 0;
    this.decisionInterval = 100; // ms
    
    // State management
    this.stateHandlers = new Map();
    this.initializeStates();
  }
  
  // Initialize state handlers - override in subclasses
  initializeStates() {
    // Base states that all units have
    this.stateHandlers.set('idle', this.handleIdleState.bind(this));
    this.stateHandlers.set('moving', this.handleMovingState.bind(this));
    this.stateHandlers.set('fleeing', this.handleFleeingState.bind(this));
  }
  
  // Main AI update loop
  update(delta, units, projectiles, resources, bases) {
    const now = Date.now();
    
    // Update perception
    if (now - this.lastPerceptionUpdate > this.perceptionUpdateInterval) {
      this.updatePerception(units, projectiles, resources, bases);
      this.lastPerceptionUpdate = now;
    }
    
    // Update threats
    if (now - this.lastThreatUpdate > this.threatUpdateInterval) {
      this.updateThreats();
      this.lastThreatUpdate = now;
    }
    
    // Make decisions
    if (now - this.lastDecision > this.decisionInterval) {
      this.makeDecision();
      this.lastDecision = now;
    }
    
    // Execute current state
    this.executeState();
  }
  
  // Perception system - gather information about surroundings
  updatePerception(units, projectiles, resources, bases) {
    // Debug: log all resources being checked (only for first unit to avoid spam)
    if (this.unit.id === 6 && resources && resources.length > 0) {
      const allResources = resources.map(r => `${r.type}:${r.active ? 'A' : 'I'}@${r.x.toFixed(0)},${r.y.toFixed(0)}`).slice(0, 10).join(' | ');
      // console.log(`All resources in game: ${allResources}`);
    }
    
    // Find nearby entities
    this.nearbyUnits = this.findNearbyEntities(units, this.perceptionRange);
    this.nearbyProjectiles = this.findNearbyEntities(projectiles, this.perceptionRange);
    this.nearbyResources = this.findNearbyEntities(resources, this.perceptionRange);
    this.nearbyBases = this.findNearbyEntities(bases, this.perceptionRange);
    
    // Separate allies and enemies
    this.nearbyAllies = this.nearbyUnits.filter(u => this.unit.isAlly(u));
    this.nearbyEnemies = this.nearbyUnits.filter(u => this.unit.isEnemy(u));
  }
  
  // Find entities within range
  findNearbyEntities(entities, range) {
    if (!entities) return [];
    
    return entities.filter(entity => {
      if (!entity || entity === this.unit) return false;
      
      // Check if entity is active/alive based on its type
      if (entity.hasOwnProperty('alive')) {
        if (entity.alive === false || entity.health <= 0) return false;
      } else if (entity.hasOwnProperty('active')) {
        if (entity.active === false) return false;
      }
      
      return this.unit.getDistance(entity) <= range;
    });
  }
  
  // Threat assessment
  updateThreats() {
    this.threats.clear();
    
    // Assess unit threats
    for (const enemy of this.nearbyEnemies) {
      const distance = this.unit.getDistance(enemy);
      const healthRatio = enemy.health / enemy.maxHealth;
      const level = enemy.level || 1;
      
      // Threat calculation: higher level, higher health, and closer = more threat
      const threatLevel = (level * healthRatio * 100) / (distance + 1);
      this.threats.set(enemy, threatLevel);
    }
    
    // Assess projectile threats
    for (const projectile of this.nearbyProjectiles) {
      if (projectile.team && projectile.team.id === this.unit.team.id) continue;
      
      // Calculate if projectile is heading towards us
      const dx = projectile.vx;
      const dy = projectile.vy;
      const toUnitX = this.unit.x - projectile.x;
      const toUnitY = this.unit.y - projectile.y;
      
      // Dot product to see if projectile is heading our way
      const dot = (dx * toUnitX + dy * toUnitY);
      if (dot > 0) {
        const distance = this.unit.getDistance(projectile);
        const speed = Math.sqrt(dx * dx + dy * dy);
        const timeToImpact = distance / speed;
        
        // Closer projectiles are higher threat
        const threatLevel = 100 / (timeToImpact + 1);
        this.threats.set(projectile, threatLevel);
      }
    }
  }
  
  // Decision making - override in subclasses
  makeDecision() {
    // Base units don't have complex decision making
    // Subclasses should override this and use their state machine
  }
  
  // Execute current state
  executeState() {
    const handler = this.stateHandlers.get(this.unit.state);
    if (handler) {
      handler();
    } else {
      debugLog(`No handler for state: ${this.unit.state}`);
      this.unit.setState('idle');
    }
  }
  
  // State handlers
  handleIdleState() {
    // Basic idle behavior - stop moving
    this.unit.stop();
  }
  
  handleMovingState() {
    // Moving is handled by specific movement goals in subclasses
  }
  
  handleFleeingState() {
    // Find the highest threat and flee from it
    let highestThreat = null;
    let highestThreatLevel = 0;
    
    for (const [entity, threatLevel] of this.threats) {
      if (threatLevel > highestThreatLevel) {
        highestThreat = entity;
        highestThreatLevel = threatLevel;
      }
    }
    
    if (highestThreat) {
      this.unit.moveAway(highestThreat.x, highestThreat.y, 1.2); // Flee faster
    } else {
      // No threats, go back to idle
      this.unit.setState('idle');
    }
  }
  
  // Utility methods
  findNearestEnemy() {
    let nearest = null;
    let nearestDistance = Infinity;
    
    for (const enemy of this.nearbyEnemies) {
      const distance = this.unit.getDistance(enemy);
      if (distance < nearestDistance) {
        nearest = enemy;
        nearestDistance = distance;
      }
    }
    
    return nearest;
  }
  
  findNearestAlly() {
    let nearest = null;
    let nearestDistance = Infinity;
    
    for (const ally of this.nearbyAllies) {
      const distance = this.unit.getDistance(ally);
      if (distance < nearestDistance) {
        nearest = ally;
        nearestDistance = distance;
      }
    }
    
    return nearest;
  }
  
  findNearestResource() {
    let nearest = null;
    let nearestDistance = Infinity;
    
    for (const resource of this.nearbyResources) {
      const distance = this.unit.getDistance(resource);
      if (distance < nearestDistance) {
        nearest = resource;
        nearestDistance = distance;
      }
    }
    
    return nearest;
  }
  
  // Check if position is safe
  isPositionSafe(x, y, threshold = 50) {
    for (const [entity, threatLevel] of this.threats) {
      const dx = x - entity.x;
      const dy = y - entity.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < threshold && threatLevel > 10) {
        return false;
      }
    }
    return true;
  }
  
  // Target validation
  isValidTarget(target) {
    if (!target || !target.alive || target.health <= 0) return false;
    if (!this.unit.isEnemy(target)) return false;
    return true;
  }
  
  // Combat helpers
  canAttack(target) {
    // Override in combat-capable subclasses
    return false;
  }
  
  shouldRetreat() {
    // Health-based retreat
    if (this.unit.health < this.unit.maxHealth * 0.2) return true;
    
    // Overwhelming odds
    const enemyStrength = this.calculateEnemyStrength();
    const allyStrength = this.calculateAllyStrength();
    
    if (enemyStrength > allyStrength * 2) return true;
    
    return false;
  }
  
  calculateEnemyStrength() {
    let strength = 0;
    for (const enemy of this.nearbyEnemies) {
      const health = enemy.health / enemy.maxHealth;
      const level = enemy.level || 1;
      strength += health * level;
    }
    return strength;
  }
  
  calculateAllyStrength() {
    let strength = 0;
    
    // Include self
    strength += (this.unit.health / this.unit.maxHealth) * (this.unit.level || 1);
    
    // Include nearby allies
    for (const ally of this.nearbyAllies) {
      const health = ally.health / ally.maxHealth;
      const level = ally.level || 1;
      strength += health * level;
    }
    return strength;
  }
  
  // Debug visualization
  renderDebug(ctx) {
    if (!isDebugMode()) return;
    
    // Draw perception range
    // ctx.strokeStyle = 'rgba(255, 255, 0, 0.2)';
    // ctx.beginPath();
    // ctx.arc(this.unit.x, this.unit.y, this.perceptionRange, 0, Math.PI * 2);
    // ctx.stroke();
    
    // Draw threats
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
    for (const [entity, threatLevel] of this.threats) {
      if (threatLevel > 10) {
        ctx.beginPath();
        ctx.moveTo(this.unit.x, this.unit.y);
        ctx.lineTo(entity.x, entity.y);
        ctx.stroke();
        
        // Draw threat level
        ctx.fillStyle = '#ff0000';
        ctx.font = '10px monospace';
        ctx.fillText(Math.round(threatLevel), entity.x, entity.y - 10);
      }
    }
  }
}